import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { CAP, useSession } from "@/auth/session";
import { Chip } from "@/components/Chip";
import { DataTable, type DataCol } from "@/components/DataTable";
import { FilePick } from "@/components/FilePick";
import { PageHead } from "@/components/PageHead";
import { SearchInput, Select } from "@/components/inputs";
import { products, type ToolReleaseRow } from "@/api/client";
import { fmtBytes } from "@/lib/format";
import { qk, useToolReleases } from "@/modules/products/data";
import { ChoosePrompt, ModelSelect, ProductSelect, ScopeBar, ScopeField, ScopeAction } from "@/modules/products/ScopeBar";

/** Tool updates — the installers a bench downloads.
 *
 *  NO MODAL, same journey as reference files: Dattatray, *"Same feedback for
 *  tool. No Pop up."* Product and target models come from the scope bar; the
 *  upload row appears inline above the table it adds to.
 *
 *  A RELEASE TARGETS MODELS (D-90), and this is the point he confirmed:
 *  *"we decided that we can allocate Tool release to a specific model — I
 *  think that was the catch (it's part of tool release rule)."* So the model
 *  selector belongs HERE, on releases, and not on the permission map, which
 *  is keyed per (product, TOOL).
 *
 *  EMPTY TARGET SET = EVERY MODEL OF THE PRODUCT, which is the opposite of
 *  `firmware_compatibility`, where no rows means nothing. Deliberate: a tool
 *  is an installer an operator runs, so an open default fails as "a bench
 *  cannot find its tool" — visible and recoverable. Firmware is flashed onto
 *  a board, where an open default bricks one.
 *
 *  DRAFT FIRST, PUBLISHED SEPARATELY. Only a published release is offered to
 *  a bench, so uploading the wrong file is a mistake you can still take back.
 *  An ARCHIVED one stops downloading entirely — the server refuses it by id,
 *  so archiving is a rule rather than a label.
 */
export function ToolUpdatesPage() {
  const { can } = useSession();
  const mayAuthor = can(CAP.MANAGE_PRODUCTS);
  const [kind, setKind] = useState("");
  const [targetModel, setTargetModel] = useState("");
  const [adding, setAdding] = useState(false);
  const q = useToolReleases(kind);
  const qc = useQueryClient();

  const setStatus = useMutation({
    mutationFn: (v: { id: string; status: string }) =>
      products.setReleaseStatus(kind, v.id, v.status),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.releases(kind) }),
  });

  const cols: DataCol<ToolReleaseRow>[] = [
    { key: "tool", label: "Tool", width: 110, render: (r) => r.tool },
    { key: "platform", label: "Platform", width: 90, render: (r) => r.platform },
    {
      key: "version", label: "Version", width: 140,
      render: (r) => <span style={{ fontFamily: "var(--mono)" }}>
        {r.version_name} <span className="cap">({r.version_code})</span>
      </span>,
    },
    {
      key: "status", label: "Status", width: 110,
      render: (r) => r.status === "published" ? <Chip tone="ok">published</Chip>
        : r.status === "archived" ? <Chip tone="mute">archived</Chip>
          : <Chip tone="warn">draft</Chip>,
    },
    {
      key: "file", label: "File", width: 210,
      render: (r) => r.status === "archived"
        ? <span style={{ fontFamily: "var(--mono)", opacity: 0.6 }}>{r.filename}</span>
        : <a href={`/modules/products/${kind}/tool-releases/${r.release_id}/download`}
            style={{ fontFamily: "var(--mono)" }}>{r.filename}</a>,
    },
    { key: "size", label: "Size", width: 90, render: (r) => fmtBytes(r.size_bytes) },
    {
      key: "sha", label: "SHA-256", width: 120, mono: true,
      render: (r) => <span title={r.sha256}>{r.sha256.slice(0, 12)}…</span>,
    },
    ...(mayAuthor ? [{
      key: "act", label: "", width: 180,
      render: (r: ToolReleaseRow) => (
        <span style={{ display: "flex", gap: 6 }}>
          {r.status !== "published" && (
            <button className="btn teal sm" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: r.release_id, status: "published" })}>
              Publish
            </button>
          )}
          {r.status !== "archived" && (
            <button className="btn ghost sm" disabled={setStatus.isPending}
              onClick={() => setStatus.mutate({ id: r.release_id, status: "archived" })}>
              Archive
            </button>
          )}
        </span>
      ),
    }] : []),
  ];

  return (
    <div className="mpage on">
      <PageHead
        eyebrow="Products · Tool updates"
        title="Tool updates"
        desc="Installers for this product's benches. Uploaded as a draft and published separately; only a published release is offered to a bench."
      />

      <ScopeBar right={mayAuthor ? (
        <button className={adding ? "btn ghost sm" : "btn teal sm"}
          disabled={!kind} onClick={() => setAdding(!adding)}>
          {adding ? "Cancel" : "Upload release"}
        </button>
      ) : undefined}>
        {/* SW-enabled products only (D-95) — a tool release is software. */}
        <ProductSelect onlyWithSoftware value={kind} onChange={(k) => { setKind(k); setTargetModel(""); }} />
        <ModelSelect kind={kind} value={targetModel} onChange={setTargetModel}
          label="Serves" />
      </ScopeBar>

      {adding && <UploadRow kind={kind} targetModel={targetModel}
        onDone={() => setAdding(false)} />}

      {setStatus.isError && (
        <div className="panel panel-pad" style={{ marginBottom: 10 }}>
          {(setStatus.error as Error).message}
        </div>
      )}

      {!kind ? <ChoosePrompt what="tool releases" /> : (
        <DataTable rows={q.data?.releases ?? []} cols={cols} rowKey={(r) => r.release_id}
          empty={<div className="panel panel-pad cap">
            {q.isLoading ? "Loading…" : "No releases for this product yet."}
          </div>} />
      )}
    </div>
  );
}

function UploadRow({ kind, targetModel, onDone }: {
  kind: string; targetModel: string; onDone: () => void;
}) {
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [tool, setTool] = useState("uds_tool");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("stable");

  const up = useMutation({
    mutationFn: async () => {
      const r = await products.uploadRelease(kind, file as File, {
        tool_slug: tool, version_code: Number(code), version_name: name, channel,
      });
      // The target set is a second call because it is a SET: the upload
      // carries one file, the targeting carries zero-or-more models, and
      // folding them into one multipart body would make "no models" and
      // "field omitted" indistinguishable — which are opposite meanings here.
      if (targetModel) {
        await products.setReleaseModels(kind, r.release_id, [targetModel]);
      }
      return r;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.releases(kind) });
      void qc.invalidateQueries({ queryKey: qk.products });
      onDone();
    },
  });

  const codeOk = /^\d+$/.test(code) && Number(code) > 0;

  return (
    <div className="panel panel-pad" style={{
      display: "flex", gap: 14, alignItems: "flex-start", flexWrap: "wrap",
      marginBottom: 12,
    }}>
      <ScopeField label="Installer" hint="its SHA-256 is computed on upload">
        <FilePick accept=".exe,.msi,.zip" label="Choose installer"
          fileName={file?.name ?? ""} onFile={setFile} />
      </ScopeField>
      <ScopeField label="Tool">
        <Select value={tool} onChange={(e) => setTool(e.target.value)}>
          <option value="uds_tool">UDS tool</option>
          <option value="eol_tool">EOL tool</option>
        </Select>
      </ScopeField>
      <ScopeField label="Version code" hint="what a bench compares — unique">
        <SearchInput value={code} mono placeholder="1042" style={{ width: 110 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCode(e.target.value)} />
      </ScopeField>
      <ScopeField label="Version name" hint="display only">
        <SearchInput value={name} placeholder="1.4.2" style={{ width: 110 }}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)} />
      </ScopeField>
      <ScopeField label="Channel">
        <Select value={channel} onChange={(e) => setChannel(e.target.value)}>
          <option value="stable">stable</option>
          <option value="beta">beta</option>
        </Select>
      </ScopeField>
      <ScopeField label="Serves"
        hint={targetModel ? "the model chosen above" : "every model of this product"}>
        <span className="cap" style={{ padding: "6px 0" }}>
          {targetModel ? <Chip tone="info">1 model</Chip> : <Chip tone="mute">all</Chip>}
        </span>
      </ScopeField>

      <ScopeAction>
        <button className="btn teal sm"
          disabled={!file || !codeOk || !name || up.isPending}
          onClick={() => up.mutate()}>
          {up.isPending ? "Uploading…" : "Upload as draft"}
        </button>
      </ScopeAction>

      {up.isError && (
        <div className="panel panel-pad" style={{ flexBasis: "100%" }}>
          {(up.error as Error).message}
        </div>
      )}
    </div>
  );
}
